import { Dispatch } from 'react';
import Bugsnag from '@bugsnag/js';

import { actions } from 'checkout/state/checkoutActions';
import { requestService } from 'shared/utils/services/requestService';
import { IOrder, IPartialOrder } from 'checkout/types/Order';
import {
  IAction,
  IErrorAction,
  IOrderSuccessAction,
  IPartialOrderSuccessAction,
} from 'checkout/types/ActionPayload';

interface IUseGiftCardProps {
  dispatch: Dispatch<IAction | IOrderSuccessAction | IPartialOrderSuccessAction | IErrorAction>;
  order?: IPartialOrder;
}

export const useGiftCard = ({ dispatch, order }: IUseGiftCardProps) => {
  const applyGiftCardToOrder = async (id: string) => {
    dispatch({ type: actions.updateOrderRequest });

    try {
      if (!id) {
        const error = new Error('Missing gift card id. Cannot apply gift card to order.');

        Bugsnag.notify(error, (event) => {
          event.severity = 'error';
          event.context = 'checkout';
          event.addMetadata('checkout', { orderNumber: order?.number });
        });

        throw error;
      }

      const response: IOrder = await requestService({
        url: `/web-api/v1/orders/${order?.number}/add_gift_card`,
        method: 'POST',
        data: { giftCardId: id },
      });

      dispatch({ type: actions.updateOrderSuccess, payload: response });

      return response;
    } catch (error) {
      dispatch({
        type: actions.updateOrderFailure,
        payload: error.message,
      });

      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
        event.addMetadata('checkout', { orderNumber: order?.number, giftCardId: id });
      });
    }
  };

  const removeGiftCardFromOrder = async (id) => {
    dispatch({ type: actions.updateOrderRequest });

    try {
      if (!id) {
        const error = new Error('Missing gift card id. Cannot remove gift card from order.');

        Bugsnag.notify(error, (event) => {
          event.severity = 'error';
          event.context = 'checkout';
          event.addMetadata('checkout', { orderNumber: order?.number });
        });

        throw error;
      }

      const response: IOrder = await requestService({
        url: `/web-api/v1/orders/${order?.number}/remove_gift_card`,
        method: 'DELETE',
        data: { giftCardId: id },
      });

      dispatch({ type: actions.updateOrderSuccess, payload: response });

      return response;
    } catch (error) {
      dispatch({
        type: actions.updateOrderFailure,
        payload: error.message,
      });

      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
        event.addMetadata('checkout', { orderNumber: order?.number, giftCardId: id });
      });
    }
  };

  return {
    applyGiftCardToOrder,
    removeGiftCardFromOrder,
  };
};
